/**
 * Карточка семьи: отображает информацию о семье и её членах
 * Дата: 09.01.2026 - Создан для TODO-029 (Patient Families)
 * Связан с: frontend/src/pages/PatientFamilies.tsx
 */
import { Card, CardHeader, CardTitle, CardContent, Button, Badge } from '@/components/ui';
import { PatientFamily } from '@/api/types';
import { Users, Phone, Edit, Trash2, Mail } from 'lucide-react';
import { formatDate } from '@/utils/formatters';
import { Can } from '@/components/Can';

interface FamilyCardProps {
  family: PatientFamily;
  onEdit: (family: PatientFamily) => void;
  onDelete: (id: string) => void;
  onSetPrimary: (familyId: string, patientId: string) => void;
}

export function FamilyCard({ family, onEdit, onDelete, onSetPrimary }: FamilyCardProps) {
  // Дата: 09.01.2026 - Исправление: удален неиспользуемый primaryMember

  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-600" />
            <CardTitle>{family.name}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-neutral-500" />
            <span className="text-sm text-neutral-600">{family.phone_normalized}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {family.members && family.members.length > 0 ? (
            <div className="space-y-2">
              {family.members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-2 bg-neutral-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    {member.is_primary || member.patient_id === family.primary_contact_id ? (
                      <Mail className="w-4 h-4 text-primary-600" />
                    ) : null}
                    <span className="text-sm font-medium">
                      {member.patient?.full_name || `Пациент ${member.patient_id.slice(0, 8)}`}
                    </span>
                    {member.role && (
                      <Badge variant="secondary" className="text-xs">
                        {member.role}
                      </Badge>
                    )}
                    {member.is_primary || member.patient_id === family.primary_contact_id ? (
                      <Badge variant="success" className="text-xs">
                        получает уведомления
                      </Badge>
                    ) : null}
                  </div>
                  {!(member.is_primary || member.patient_id === family.primary_contact_id) && (
                    <Can permission="families:write">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onSetPrimary(family.id, member.patient_id)}
                        className="text-xs"
                      >
                        Сделать основным
                      </Button>
                    </Can>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-neutral-500">Нет членов семьи</p>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-neutral-200">
            <span className="text-xs text-neutral-500">
              Создана: {formatDate(family.created_at)}
            </span>
            <div className="flex gap-2">
              <Can permission="families:write">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onEdit(family)}
                  testId={`btn-edit-family-${family.id}`}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Редактировать
                </Button>
              </Can>
              <Can permission="families:delete">
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => onDelete(family.id)}
                  testId={`btn-delete-family-${family.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Удалить
                </Button>
              </Can>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

